import React from 'react';
import { Alert, AlertTitle, Box, Button, Skeleton, TableCell, TableRow, Typography } from '@mui/material';
import type { UsageErrorReport } from '../types';
import { LEER, formatCount, formatDay, rangeText } from '../services/usageFormat';
import SchutzZeile from './SchutzZeile';
import Abschnitt from './Abschnitt';
import DatenTabelle, { zahlenZelle } from './DatenTabelle';
import { nutzerText } from './SeitenTabelle';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c) — im Browser gemeldete Fehler.
 *
 * Eigenes Dokument, eigener Lade- und Fehlerzustand und ein EIGENER Zeitraum: Fehler kommen aus
 * den Rohdaten, deren Frist kürzer ist als die der verdichteten Tageszahlen. Der Abschnitt nennt
 * sein Fenster deshalb selbst — sonst widerspräche er scheinbar dem Seitenkopf.
 *
 * „Zuletzt“ ist tagesgenau. Eine Uhrzeit gibt die API bewusst nicht her: bei sechs betroffenen
 * Personen wäre eine Minute ein Fingerabdruck.
 */
export interface FehlerAbschnittProps {
  days: number;
  report: UsageErrorReport | undefined;
  loading: boolean;
  error: Error | undefined;
  onErneut: () => void;
}

export default function FehlerAbschnitt({ days, report, loading, error, onErneut }: FehlerAbschnittProps) {
  return (
    <Abschnitt
      titel="Fehler"
      testId="nutzung-fehler-abschnitt"
      erlaeuterung="Im Browser gemeldete Fehler (Fehlergrenze, Fenster, Promises, GraphQL); der Wortlaut ist beim Erfassen bereinigt und gekürzt. Der Zeitraum ist kürzer als oben, weil diese Zahlen aus den Rohdaten stammen und nicht aus der Tagesverdichtung."
    >
      {loading && (
        <Box>
          <Skeleton width={320} />
          <Skeleton variant="rectangular" height={120} sx={{ mt: 1, borderRadius: 2 }} />
        </Box>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          data-testid="nutzung-fehler-meldung"
          action={
            <Button color="inherit" size="small" onClick={onErneut}>
              Erneut versuchen
            </Button>
          }
        >
          <AlertTitle>Die Fehlerliste konnte nicht geladen werden.</AlertTitle>
          {error.message}
        </Alert>
      )}

      {!loading && !error && report && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {rangeText({ days, dataFrom: report.dataFrom, dataUntil: report.dataUntil, missingDays: [] })}
          </Typography>
          {report.truncated && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Im Zeitraum liegen mehr als 50 000 Rohzeilen. Gelesen wurden die jüngsten; die Zahlen sind Untergrenzen.
            </Alert>
          )}
          <DatenTabelle
            leer={report.items.length === 0}
            leerText="Im Zeitraum wurden keine Fehler gemeldet."
            spalten={[
              { titel: 'Meldung' },
              { titel: 'Route' },
              { titel: 'Quelle' },
              { titel: 'Anzahl', numerisch: true },
              { titel: 'Nutzer', numerisch: true },
              { titel: 'Zuletzt' },
            ]}
          >
            {report.items.map((row, stelle) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={`${row.label}-${stelle}`}>
                <TableCell>
                  <SchutzZeile
                    suppressed={row.suppressed}
                    groups={row.groups}
                    users={row.users}
                    bezeichner={row.message}
                    technisch
                  />
                </TableCell>
                <TableCell>
                  {row.suppressed ? (
                    LEER
                  ) : (
                    <SchutzZeile
                      suppressed={false}
                      groups={row.groups}
                      users={row.users}
                      bezeichner={row.route}
                      technisch
                    />
                  )}
                </TableCell>
                <TableCell>{row.suppressed ? LEER : (row.source ?? LEER)}</TableCell>
                <TableCell sx={zahlenZelle}>{formatCount(row.count)}</TableCell>
                <TableCell sx={zahlenZelle}>{nutzerText(row.users, row.usersEstimated)}</TableCell>
                <TableCell>{formatDay(row.lastSeenDay)}</TableCell>
              </TableRow>
            ))}
          </DatenTabelle>
        </>
      )}
    </Abschnitt>
  );
}
